import { ChangeDetectionStrategy, Component, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { SPRINT_4_CONTENT, CanvasBlock } from '../data/course-data';

@Component({
  selector: 'app-sprint4-section',
  standalone: true,
  imports: [CommonModule, FormsModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <section class="py-12 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto space-y-12">
      <!-- Section Header -->
      <div class="bg-[#121214] rounded-2xl p-6 sm:p-8 border border-white/10 shadow-xl relative overflow-hidden">
        <div class="inline-flex items-center space-x-1.5 px-3 py-1 rounded-full bg-[#F27D26]/10 text-[#F27D26] text-xs font-semibold mb-3 border border-[#F27D26]/30">
          <span class="material-symbols-outlined text-sm">account_tree</span>
          <span>SPRINT 4 (10h) • O 'DEPLOY' DO MODELO</span>
        </div>
        <h2 class="text-2xl sm:text-3xl font-extrabold text-white tracking-tight mb-2">
          {{ content.title }}
        </h2>
        <p class="text-zinc-400 text-sm max-w-3xl">
          {{ content.subtitle }} — Modelagem visual e estruturação da arquitetura de negócios em 1 página antes de redigir o Plano de Negócios para captação de investimento.
        </p>
      </div>

      <!-- Business Model Canvas Interativo -->
      <div class="bg-blueprint-grid rounded-2xl border border-white/10 p-6 sm:p-8 text-white shadow-xl space-y-8">
        <div class="flex flex-col sm:flex-row sm:items-center justify-between gap-4 border-b border-white/10 pb-4">
          <div>
            <span class="text-xs font-mono font-medium text-[#F27D26] uppercase tracking-wider block mb-1">
              • ARQUITETURA DE NEGÓCIO EM 1 PÁGINA
            </span>
            <h3 class="text-xl sm:text-2xl font-bold text-white flex items-center space-x-2">
              <span class="material-symbols-outlined text-[#F27D26]">grid_view</span>
              <span>Business Model Canvas do Seu Negócio em Redes</span>
            </h3>
            <p class="text-xs text-zinc-400 mt-1">{{ content.canvasArchitectureIntro }}</p>
          </div>

          <!-- Conceptual indicator tag -->
          <div class="inline-flex items-center space-x-1 px-3 py-1.5 rounded-xl bg-white/5 border border-white/10 text-xs font-mono text-zinc-300 shrink-0">
            <span class="material-symbols-outlined text-sm text-[#F27D26]">lightbulb</span>
            <span>Estrutura Conceitual</span>
          </div>
        </div>

        <!-- 9 Building Blocks Grid Representation -->
        <div class="grid grid-cols-1 md:grid-cols-5 gap-4">
          <!-- Col 1: Parcerias (Infra) -->
          <div class="md:col-span-1 bg-[#0A0A0B] rounded-xl p-4 border border-white/10 hover:border-[#F27D26]/50 transition-all flex flex-col justify-between space-y-3">
            <div>
              <div class="flex items-center space-x-2 text-[#F27D26] mb-2">
                <span class="material-symbols-outlined text-xl">{{ getBlock('partnerships').icon }}</span>
                <label for="block_partnerships" class="font-bold text-xs uppercase font-mono cursor-pointer text-white">{{ getBlock('partnerships').title }}</label>
              </div>
              <p class="text-[10px] text-zinc-400 mb-2">{{ getBlock('partnerships').subtitle }}</p>
              <textarea 
                id="block_partnerships"
                rows="8"
                [ngModel]="getBlock('partnerships').content"
                (ngModelChange)="updateBlockContent('partnerships', $event)"
                (input)="autoResize($event)"
                [placeholder]="getBlock('partnerships').placeholder"
                class="w-full bg-[#121214] p-3 rounded-lg border border-white/10 text-xs sm:text-sm text-white focus:ring-1 focus:ring-[#F27D26] focus:outline-none resize-none overflow-hidden leading-relaxed min-h-[160px]"></textarea>
            </div>
          </div>

          <!-- Col 2: Atividades & Recursos (Infra) -->
          <div class="md:col-span-1 flex flex-col gap-4">
            <!-- Atividades -->
            <div class="bg-[#0A0A0B] rounded-xl p-4 border border-white/10 hover:border-[#F27D26]/50 transition-all flex-1">
              <div class="flex items-center space-x-2 text-[#F27D26] mb-1">
                <span class="material-symbols-outlined text-xl">{{ getBlock('activities').icon }}</span>
                <label for="block_activities" class="font-bold text-xs uppercase font-mono cursor-pointer text-white">{{ getBlock('activities').title }}</label>
              </div>
              <textarea 
                id="block_activities"
                rows="6"
                [ngModel]="getBlock('activities').content"
                (ngModelChange)="updateBlockContent('activities', $event)"
                (input)="autoResize($event)"
                [placeholder]="getBlock('activities').placeholder"
                class="w-full bg-[#121214] p-3 rounded-lg border border-white/10 text-xs sm:text-sm text-white focus:ring-1 focus:ring-[#F27D26] focus:outline-none resize-none overflow-hidden leading-relaxed mt-2 min-h-[140px]"></textarea>
            </div>

            <!-- Recursos -->
            <div class="bg-[#0A0A0B] rounded-xl p-4 border border-white/10 hover:border-[#F27D26]/50 transition-all flex-1">
              <div class="flex items-center space-x-2 text-[#F27D26] mb-1">
                <span class="material-symbols-outlined text-xl">{{ getBlock('resources').icon }}</span>
                <label for="block_resources" class="font-bold text-xs uppercase font-mono cursor-pointer text-white">{{ getBlock('resources').title }}</label>
              </div>
              <textarea 
                id="block_resources"
                rows="6"
                [ngModel]="getBlock('resources').content"
                (ngModelChange)="updateBlockContent('resources', $event)"
                (input)="autoResize($event)"
                [placeholder]="getBlock('resources').placeholder"
                class="w-full bg-[#121214] p-3 rounded-lg border border-white/10 text-xs sm:text-sm text-white focus:ring-1 focus:ring-[#F27D26] focus:outline-none resize-none overflow-hidden leading-relaxed mt-2 min-h-[140px]"></textarea>
            </div>
          </div>

          <!-- Col 3: Proposta de Valor (Core) -->
          <div class="md:col-span-1 bg-[#F27D26]/10 rounded-xl p-4 border border-[#F27D26]/40 hover:border-[#F27D26] transition-all flex flex-col justify-between">
            <div>
              <div class="flex items-center space-x-2 text-[#F27D26] mb-2">
                <span class="material-symbols-outlined text-2xl">{{ getBlock('value_prop').icon }}</span>
                <label for="block_value_prop" class="font-bold text-xs uppercase font-mono cursor-pointer text-white">{{ getBlock('value_prop').title }}</label>
              </div>
              <p class="text-[10px] text-[#F27D26] font-semibold mb-2">⭐ {{ getBlock('value_prop').subtitle }}</p>
              <textarea 
                id="block_value_prop"
                rows="11"
                [ngModel]="getBlock('value_prop').content"
                (ngModelChange)="updateBlockContent('value_prop', $event)"
                (input)="autoResize($event)"
                [placeholder]="getBlock('value_prop').placeholder"
                class="w-full bg-[#0A0A0B] p-3 rounded-lg border border-[#F27D26]/30 text-xs sm:text-sm text-white focus:ring-1 focus:ring-[#F27D26] focus:outline-none resize-none overflow-hidden leading-relaxed min-h-[220px]"></textarea>
            </div>
          </div>

          <!-- Col 4: Relacionamento & Canais (Market) -->
          <div class="md:col-span-1 flex flex-col gap-4">
            <!-- Relacionamento -->
            <div class="bg-[#0A0A0B] rounded-xl p-4 border border-white/10 hover:border-[#F27D26]/50 transition-all flex-1">
              <div class="flex items-center space-x-2 text-[#F27D26] mb-1">
                <span class="material-symbols-outlined text-xl">{{ getBlock('relationships').icon }}</span>
                <label for="block_relationships" class="font-bold text-xs uppercase font-mono cursor-pointer text-white">{{ getBlock('relationships').title }}</label>
              </div>
              <textarea 
                id="block_relationships"
                rows="6"
                [ngModel]="getBlock('relationships').content"
                (ngModelChange)="updateBlockContent('relationships', $event)"
                (input)="autoResize($event)"
                [placeholder]="getBlock('relationships').placeholder"
                class="w-full bg-[#121214] p-3 rounded-lg border border-white/10 text-xs sm:text-sm text-white focus:ring-1 focus:ring-[#F27D26] focus:outline-none resize-none overflow-hidden leading-relaxed mt-2 min-h-[140px]"></textarea>
            </div>

            <!-- Canais -->
            <div class="bg-[#0A0A0B] rounded-xl p-4 border border-white/10 hover:border-[#F27D26]/50 transition-all flex-1">
              <div class="flex items-center space-x-2 text-[#F27D26] mb-1">
                <span class="material-symbols-outlined text-xl">{{ getBlock('channels').icon }}</span>
                <label for="block_channels" class="font-bold text-xs uppercase font-mono cursor-pointer text-white">{{ getBlock('channels').title }}</label>
              </div>
              <textarea 
                id="block_channels"
                rows="6"
                [ngModel]="getBlock('channels').content"
                (ngModelChange)="updateBlockContent('channels', $event)"
                (input)="autoResize($event)"
                [placeholder]="getBlock('channels').placeholder"
                class="w-full bg-[#121214] p-3 rounded-lg border border-white/10 text-xs sm:text-sm text-white focus:ring-1 focus:ring-[#F27D26] focus:outline-none resize-none overflow-hidden leading-relaxed mt-2 min-h-[140px]"></textarea>
            </div>
          </div>

          <!-- Col 5: Segmentos de Clientes (Market) -->
          <div class="md:col-span-1 bg-[#0A0A0B] rounded-xl p-4 border border-white/10 hover:border-[#F27D26]/50 transition-all flex flex-col justify-between space-y-3">
            <div>
              <div class="flex items-center space-x-2 text-[#F27D26] mb-2">
                <span class="material-symbols-outlined text-xl">{{ getBlock('segments').icon }}</span>
                <label for="block_segments" class="font-bold text-xs uppercase font-mono cursor-pointer text-white">{{ getBlock('segments').title }}</label>
              </div>
              <p class="text-[10px] text-zinc-400 mb-2">{{ getBlock('segments').subtitle }}</p>
              <textarea 
                id="block_segments"
                rows="8"
                [ngModel]="getBlock('segments').content"
                (ngModelChange)="updateBlockContent('segments', $event)"
                (input)="autoResize($event)"
                [placeholder]="getBlock('segments').placeholder"
                class="w-full bg-[#121214] p-3 rounded-lg border border-white/10 text-xs sm:text-sm text-white focus:ring-1 focus:ring-[#F27D26] focus:outline-none resize-none overflow-hidden leading-relaxed min-h-[160px]"></textarea>
            </div>
          </div>
        </div>

        <!-- Bottom Row: Base (Custos & Receitas) -->
        <div class="grid grid-cols-1 md:grid-cols-2 gap-4 pt-2">
          <!-- Estrutura de Custos -->
          <div class="bg-[#0A0A0B] rounded-xl p-4 border border-white/10 hover:border-rose-500/50 transition-all">
            <div class="flex items-center space-x-2 text-rose-400 mb-2">
              <span class="material-symbols-outlined text-xl">{{ getBlock('cost_structure').icon }}</span>
              <label for="block_cost_structure" class="font-bold text-xs uppercase font-mono cursor-pointer text-white">{{ getBlock('cost_structure').title }}</label>
            </div>
            <textarea 
              id="block_cost_structure"
              rows="6"
              [ngModel]="getBlock('cost_structure').content"
              (ngModelChange)="updateBlockContent('cost_structure', $event)"
              (input)="autoResize($event)"
              [placeholder]="getBlock('cost_structure').placeholder"
              class="w-full bg-[#121214] p-3 rounded-lg border border-white/10 text-xs sm:text-sm text-white focus:ring-1 focus:ring-rose-400 focus:outline-none resize-none overflow-hidden leading-relaxed min-h-[140px]"></textarea>
          </div>

          <!-- Fontes de Receita -->
          <div class="bg-[#0A0A0B] rounded-xl p-4 border border-white/10 hover:border-emerald-500/50 transition-all">
            <div class="flex items-center space-x-2 text-emerald-400 mb-2">
              <span class="material-symbols-outlined text-xl">{{ getBlock('revenue_streams').icon }}</span>
              <label for="block_revenue_streams" class="font-bold text-xs uppercase font-mono cursor-pointer text-white">{{ getBlock('revenue_streams').title }}</label>
            </div>
            <textarea 
              id="block_revenue_streams"
              rows="6"
              [ngModel]="getBlock('revenue_streams').content"
              (ngModelChange)="updateBlockContent('revenue_streams', $event)"
              (input)="autoResize($event)"
              [placeholder]="getBlock('revenue_streams').placeholder"
              class="w-full bg-[#121214] p-3 rounded-lg border border-white/10 text-xs sm:text-sm text-white focus:ring-1 focus:ring-emerald-400 focus:outline-none resize-none overflow-hidden leading-relaxed min-h-[140px]"></textarea>
          </div>
        </div>
      </div>

      <!-- Estruturação para Escala -->
      <div class="bg-[#121214] rounded-2xl p-6 sm:p-8 border border-white/10 shadow-xl space-y-6">
        <div class="border-b border-white/10 pb-4">
          <span class="text-xs font-mono font-semibold text-[#F27D26] uppercase tracking-wider block mb-1">
            MIGRAÇÃO DE ETAPA
          </span>
          <h3 class="text-2xl font-extrabold text-white">
            Estruturação para Escala: Do Canvas ao Plano de Negócios
          </h3>
          <p class="text-zinc-400 text-xs mt-1">
            Como filtrar hipóteses no Canvas e convertê-las em um Plano de Negócios robusto para investidores e expansão da infraestrutura.
          </p>
        </div>

        <div class="grid grid-cols-1 md:grid-cols-12 gap-6 items-center">
          <!-- Canvas Phase -->
          <div class="md:col-span-5 p-6 rounded-2xl bg-[#0A0A0B] border border-white/10 space-y-3">
            <div class="flex items-center space-x-2 text-[#F27D26] mb-1">
              <span class="material-symbols-outlined">grid_view</span>
              <h4 class="font-bold text-lg text-white">{{ content.scalingProcess.canvas.title }}</h4>
            </div>
            <p class="text-xs text-zinc-300"><strong>Foco:</strong> {{ content.scalingProcess.canvas.focus }}</p>
            <p class="text-xs text-zinc-400"><strong>Características:</strong> {{ content.scalingProcess.canvas.characteristics }}</p>
            <p class="text-xs text-zinc-400"><strong>Uso:</strong> {{ content.scalingProcess.canvas.usage }}</p>
          </div>

          <!-- Transition Funnel Arrow -->
          <div class="md:col-span-2 flex flex-col items-center justify-center p-4 text-center">
            <div class="w-12 h-12 rounded-full bg-[#F27D26]/10 border border-[#F27D26]/30 text-[#F27D26] flex items-center justify-center mb-2">
              <span class="material-symbols-outlined text-2xl">filter_alt</span>
            </div>
            <span class="text-[10px] font-mono text-[#F27D26] font-bold uppercase">Validação de Hipóteses</span>
            <span class="material-symbols-outlined text-[#F27D26] text-xl hidden md:block">arrow_forward</span>
            <span class="material-symbols-outlined text-[#F27D26] text-xl block md:hidden">arrow_downward</span>
          </div>

          <!-- Business Plan Phase -->
          <div class="md:col-span-5 p-6 rounded-2xl bg-[#F27D26]/10 border border-[#F27D26]/30 space-y-3">
            <div class="flex items-center space-x-2 text-[#F27D26] mb-1">
              <span class="material-symbols-outlined">auto_stories</span>
              <h4 class="font-bold text-lg text-white">{{ content.scalingProcess.businessPlan.title }}</h4>
            </div>
            <p class="text-xs text-zinc-200"><strong>Foco:</strong> {{ content.scalingProcess.businessPlan.focus }}</p>
            <p class="text-xs text-zinc-300"><strong>Características:</strong> {{ content.scalingProcess.businessPlan.characteristics }}</p>
            <p class="text-xs text-zinc-300"><strong>Uso:</strong> {{ content.scalingProcess.businessPlan.usage }}</p>
          </div>
        </div>
      </div>
    </section>
  `
})
export class Sprint4SectionComponent {
  content = SPRINT_4_CONTENT;

  canvasBlocks = signal<CanvasBlock[]>([...SPRINT_4_CONTENT.canvasDefaultBlocks]);

  getBlock(id: string): CanvasBlock {
    const found = this.canvasBlocks().find(b => b.id === id);
    if (found) return found;
    return {
      id,
      title: 'Bloco',
      subtitle: '',
      category: 'core',
      icon: 'grid_view',
      placeholder: '',
      content: ''
    };
  }

  updateBlockContent(id: string, newContent: string) {
    this.canvasBlocks.update(list => 
      list.map(b => b.id === id ? { ...b, content: newContent } : b)
    );
  }

  autoResize(event: Event) {
    const el = event.target as HTMLTextAreaElement;
    if (el) {
      el.style.height = 'auto';
      el.style.height = el.scrollHeight + 'px';
    }
  }

  loadExampleCanvas() {
    this.canvasBlocks.set([
      {
        id: "partnerships",
        title: "Parcerias Principais",
        subtitle: "Lado Esquerdo (Infra/Operação)",
        category: "infra",
        icon: "handshake",
        placeholder: "",
        content: "• Provedores Cloud (AWS, GCP)\n• Fabricantes de hardware Mesh (MikroTik, Ubiquiti)\n• Integradores B2B regionais"
      },
      {
        id: "activities",
        title: "Atividades-Chave",
        subtitle: "Lado Esquerdo (Infra/Operação)",
        category: "infra",
        icon: "build",
        placeholder: "",
        content: "• P&D do algoritmo de otimização de rotas SD-WAN\n• Suporte automatizado NOC 24/7\n• Vendas diretas enterprise"
      },
      {
        id: "resources",
        title: "Recursos-Chave",
        subtitle: "Lado Esquerdo (Infra/Operação)",
        category: "infra",
        icon: "inventory_2",
        placeholder: "",
        content: "• Algoritmo de roteamento proprietário\n• Time de engenharia de software e redes\n• Servidores orquestradores em nuvem"
      },
      {
        id: "value_prop",
        title: "Proposta de Valor",
        subtitle: "Centro (O Core)",
        category: "core",
        icon: "memory",
        placeholder: "",
        content: "Garantir 99.99% de uptime para empresas de médio porte via SD-WAN inteligente com failover automático para rede 5G em menos de 500ms, reduzindo custos de link dedicado em até 60%."
      },
      {
        id: "relationships",
        title: "Relacionamento com Clientes",
        subtitle: "Lado Direito (Rede/Mercado)",
        category: "market",
        icon: "favorite",
        placeholder: "",
        content: "• Painel self-service SaaS com telemetria em tempo real\n• Gerente de conta dedicado para planos corporativos"
      },
      {
        id: "channels",
        title: "Canais de Distribuição",
        subtitle: "Lado Direito (Rede/Mercado)",
        category: "market",
        icon: "router",
        placeholder: "",
        content: "• Portal Web e API aberta\n• Vendas B2B consultivas\n• Canais de revenda de TI"
      },
      {
        id: "segments",
        title: "Segmentos de Clientes",
        subtitle: "Lado Direito (Rede/Mercado)",
        category: "market",
        icon: "groups",
        placeholder: "",
        content: "• Startups e PMEs com operação 100% digital\n• Redes de franquias e varejo com múltiplas filiais\n• Provedores de internet locais buscando serviços adicionais"
      },
      {
        id: "cost_structure",
        title: "Estrutura de Custos",
        subtitle: "Base (O Servidor/Finanças)",
        category: "finance",
        icon: "receipt_long",
        placeholder: "",
        content: "• Infraestrutura de servidores e tráfego na nuvem\n• Folha de pagamento técnica e vendas\n• Marketing digital e licenciamento"
      },
      {
        id: "revenue_streams",
        title: "Fontes de Receita",
        subtitle: "Base (O Servidor/Finanças)",
        category: "finance",
        icon: "attach_money",
        placeholder: "",
        content: "• Mensalidade SaaS recorrente por roteador gerenciado (MRR)\n• Setup e instalação inicial\n• Suporte SLA Premium 24h"
      }
    ]);
  }

  clearCanvas() {
    this.canvasBlocks.set([...SPRINT_4_CONTENT.canvasDefaultBlocks]);
  }
}
